import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, MapPin, Phone, Store } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { getProfileUpdate, AllCustomerShopping } from '../../services/api';

const AdminBusinessDetails = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fallbackEmail = location.state?.email;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError('');
        // Try filtering by profile_id first, then by email
        const payload = profileId ? { profile_id: profileId } : (fallbackEmail ? { email: fallbackEmail } : {});
        const resp = await getProfileUpdate(payload);
        const list = Array.isArray(resp.extractedData) ? resp.extractedData : (resp.extractedData?.records || []);

           let txAgg = {};
                  try {
                    const txResp = await AllCustomerShopping({});
                    const txList = Array.isArray(txResp.extractedData) ? txResp.extractedData : (txResp.extractedData?.records || []);
                    txAgg = txList.reduce((acc, t) => {
                      // Primary join key is email_id per requirement; normalize to lowercase
                      const rawKey = t.email_id || t.email || t.businessEmail || t.business_email || t.vendorEmail || t.vendor_email || t.profile_id || t.profileId || null;
                      const key = typeof rawKey === 'string' ? rawKey.trim().toLowerCase() : rawKey;
                      if (!key) return acc;
                      if (!acc[key]) acc[key] = { totalAmount: 0, pointBalanceSum: 0 };
                      const amt = Number(t.amount ?? t.total ?? 0);
                      const pb = Number(t.point_balance ?? t.pointBalance ?? 0);
                      if (isFinite(amt)) acc[key].totalAmount += amt;
                      if (isFinite(pb)) acc[key].pointBalanceSum += pb;
                      return acc;
                    }, {});
                  } catch (e) {
                    // If transaction API fails, leave txAgg empty and fall back to zeros
                    txAgg = {};
                  }
        // find best match
        let item = null;
        if (profileId) {
          item = list.find((r) => (r.profile_id || r.id) == profileId) || list[0];
        } else if (fallbackEmail) {
          item = list.find((r) => (r.email || r.email_id) === fallbackEmail) || list[0];
        } else {
          item = list[0];
        }

        if (!item) {
          setError('No business found');
          setBusiness(null);
        } else {
          // Normalize
          const normalized = {
            id: item.profile_id || item.id || '',
            businessName: item.businessName || (item.firstName ? `${item.firstName} ${item.lastName || ''}`.trim() : (item.displayName || item.name || 'Business')),
            email: item.email || item.email_id || '',
            phone: item.phone || item.mobile || '',
            address: (() => {
              if (typeof item.address === 'string') return item.address;
              const loc = item.location || item.loc || null;
              if (loc && typeof loc === 'object') {
                const lat = loc.lat ?? loc.latitude;
                const lng = loc.lng ?? loc.longitude;
                if (lat != null && lng != null) return `${lat}, ${lng}`;
              }
              return '';
            })(),
            // availablePoints: item.availablePoints ?? item.totalPoints ?? item.points ?? 0,
            // totalPurchases: item.totalPurchases ?? 0,
            availablePoints: (() => {
              const keyEmail = (item.email || item.email_id || '').toLowerCase();
              if (keyEmail && txAgg[keyEmail]) return txAgg[keyEmail].pointBalanceSum || 0;
              return 0;
            })(),
            totalPurchases: (() => {
              const keyEmail = (item.email || item.email_id || '').toLowerCase();
              if (keyEmail && txAgg[keyEmail]) return txAgg[keyEmail].totalAmount || 0;
              return 0;
            })(),
            // totalIncome: item.totalIncome ?? 0,
            createdAt: item.created_on || item.createdAt || item.createdDate || null,
            shopDetails: Array.isArray(item.shopDetails) ? item.shopDetails : (item.shops || []),
          };

          // If there are no shops for this business, return to list instead of showing empty details
          const hasShops = Array.isArray(normalized.shopDetails) && normalized.shopDetails.length > 0;
          if (!hasShops) {
            setBusiness(null);
            setLoading(false);
            navigate('/admin/business');
            return;
          }

          setBusiness(normalized);
        }
      } catch (e) {
        console.error('Failed to load business details', e);
        setError('Failed to load business details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [profileId, fallbackEmail]);

  // const createdAtText = useMemo(() => {
  //   if (!business?.createdAt) return '-';
  //   try { return new Date(business.createdAt).toLocaleString(); } catch { return String(business.createdAt); }
  // }, [business?.createdAt]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="Business Details" showBack onBack={() => navigate('/admin/business')} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <button onClick={() => navigate('/admin/business')} className="flex items-center space-x-2 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg border">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Business List</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-md">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Business Details</h2>
                {business && <p className="text-sm text-gray-500">Profile ID: {business.id}</p>}
              </div>
            </div>
            {loading && <span className="text-sm text-gray-500">Loading...</span>}
          </div>

          {error && (
            <div className="px-6 py-4 text-red-600">{error}</div>
          )}

          {!loading && business && (
            <div className="p-6">
              {/* Top summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-700"><Mail className="w-4 h-4" /><span>{business.email || '-'}</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><Phone className="w-4 h-4" /><span>{business.phone || '-'}</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><MapPin className="w-4 h-4" /><span>{business.address || '-'}</span></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500">Available Points</div>
                    <div className="text-xl font-semibold">{Number(business.availablePoints).toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-xs text-gray-500">Total Purchases</div>
                    <div className="text-xl font-semibold">{Number(business.totalPurchases).toLocaleString()}</div>
                  </div>
                  {/* <div className="bg-gray-50 rounded-lg p-4 text-center"> */}
                    {/* <div className="text-xs text-gray-500">Total Income</div> */}
                    {/* <div className="text-xl font-semibold">₹{Number(business.totalIncome).toLocaleString()}</div> */}
                  {/* </div> */}
                </div>
              </div>

              {/* <div className="mb-6 text-sm text-gray-500">Created: {createdAtText}</div> */}

              {/* Shop Details */}
              <div className="bg-white rounded-lg border">
                <div className="px-6 py-4 border-b flex items-center gap-2">
                  <Store className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Shop Details</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(business.shopDetails && business.shopDetails.length > 0) ? (
                        business.shopDetails.map((s, idx) => {
                          const addr = (() => {
                            if (typeof s.address === 'string' && s.address.trim()) return s.address;
                            const loc = s.location || {};
                            if (loc && typeof loc === 'object') {
                              const lat = loc.lat ?? loc.latitude;
                              const lng = loc.lng ?? loc.longitude;
                              if (lat != null && lng != null) return `${lat}, ${lng}`;
                            }
                            // If location is a primitive string
                            if (typeof s.location === 'string') return s.location;
                            return '-';
                          })();
                          const contact = s.phone || s.mobile || s.contact || '-';
                          return (
                            <tr key={idx}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{s.shopName || s.name || '-'}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{addr}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td className="px-6 py-6 text-center text-gray-500" colSpan={3}>No shops available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBusinessDetails;
